import { Box, Text } from "@chakra-ui/react";
import React from "react";
import { CircleCheck, Lock } from "lucide-react";
import { useAnimationToggle } from "../../Contexts/AnimationContext";
import { useKiosk } from "../../Contexts/KioskContext";

/*
  A selectable card component used to display a tool's information.
*/
function ToolCard({ isReadOnly = false, type, name, isSelected, onClick, isDisabled, accessibleLabel = "Tool", onUnavailable, unavailableLabel }) {
  const { isKiosk } = useKiosk();

  let backgroundColor = "white";
  let textColor = "gray.800";
  if (isDisabled) {
    backgroundColor = "gray.50";
    textColor = "gray.600";
  } else if (isSelected) {
    backgroundColor = "semantic.successBg";
    textColor = "gray.800";
  }

  const { animationsEnabled } = useAnimationToggle();

  const handleKeyDown = (e) => {
    if (isReadOnly) return;
    // Arrow keys are handled at container level
    if ((onClick || onUnavailable) && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      if (isDisabled) onUnavailable?.();
      else onClick?.();
    }
  };

  return (
    <Box
      position="relative"
      onClick={isReadOnly ? undefined : isDisabled ? onUnavailable : onClick}
      onKeyDown={handleKeyDown}
      tabIndex={isReadOnly || (isDisabled && !onUnavailable) ? -1 : 0}
      role="button"
      aria-label={`${accessibleLabel}: ${name}${isSelected ? ", selected" : ""}${isDisabled ? onUnavailable ? ", unavailable; show details" : ", disabled" : ""}`}
      aria-pressed={isSelected}
      aria-disabled={isReadOnly || (isDisabled && !onUnavailable) ? true : undefined}
      cursor={!isDisabled || onUnavailable ? "pointer" : undefined}
      sx={{
        "@media (hover: hover)": {
          "&:hover": animationsEnabled && !isDisabled ? {
            borderColor: isSelected ? "green.600" : "gray.300",
            shadow: "lg",
            transition: "all 0.15s",
          } : {},
        },
      }}
      _active={!isReadOnly && (!isDisabled || onUnavailable) ? { boxShadow: "inset 0 0 0 999px rgb(0 0 0 / 12%)" } : {}}
      _focus={
        (!isDisabled || onUnavailable)
          ? {
              outline: isKiosk ? "3px solid" : "2px solid",
              outlineColor: "blue.500",
              outlineOffset: "2px",
            }
          : {}
      }
      w="full"
      p={isKiosk ? 4 : 5}
      pr={isKiosk ? 8 : 5}
      shadow={isSelected ? "md" : "sm"}
      border={isKiosk ? "2px solid" : "1px solid"}
      borderColor={isSelected ? "green.500" : "gray.200"}
      rounded="xl"
      bg={backgroundColor}
      color={textColor}
      opacity={1}
      transition="all 0.15s"
    >
      {isSelected && (
        <Box position="absolute" top={2} right={2}>
          <Box as={CircleCheck} color="green.500" boxSize={5} aria-hidden="true" />
        </Box>
      )}
      {isDisabled && (
        <Box position="absolute" top={2} right={2}>
          <Box as={Lock} color="gray.400" boxSize={isKiosk ? 5 : 4} aria-hidden="true" />
        </Box>
      )}

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minH={isKiosk ? 12 : 16}
      >
        <Text
          fontSize={isKiosk ? "lg" : "lg"}
          fontWeight={700}
          noOfLines={isKiosk ? undefined : 2}
          maxW="100%"
          textAlign="center"
          lineHeight="short"
        >
          {name}
        </Text>
      </Box>
      {isDisabled && unavailableLabel === "Out of service" && <Text mt={2} fontSize="md" textAlign="center" color="gray.600">{unavailableLabel}</Text>}
    </Box>
  );
}

export default ToolCard;
